// src/oura/components/search/format.ts
//
// Number formatting for Search performance. A missing value is an en dash,
// never 0: "not measured" and "none" are different claims.

import type { AeoSearchBuckets, AeoSearchKeyword, AeoTrafficInsights } from "@service/aeo";
import { EMPTY_CELL } from "../client/staffScreen";

/**
 * How many searches for what the business sells (not its name) the site
 * shows up for: the bands are cumulative, so the top 20 plus those below it
 * is every one of them. Null when the payload carries no bands.
 */
export function searchesForWhatYouSell(buckets: AeoSearchBuckets | undefined): number | null {
    return buckets ? buckets.top20 + buckets.beyond20 : null;
}

/** A fraction as a percent: 0.0225 -> "2.3%". */
export function pct(value: number | null | undefined, digits = 1): string {
    if (value === null || value === undefined) return EMPTY_CELL;
    return `${(value * 100).toFixed(digits)}%`;
}

export function num(value: number | null | undefined): string {
    if (value === null || value === undefined) return EMPTY_CELL;
    return value.toLocaleString("en-GB");
}

/** An average position to one place: 9.43 -> "9.4". */
export function position(value: number | null | undefined): string {
    if (value === null || value === undefined) return EMPTY_CELL;
    return value.toFixed(1);
}

/** Ranking band colour, on the platform's semantic scale: top 3, top 10, top 20, past it. */
export function positionTone(value: number): string {
    if (value <= 3) return "text-emerald-700";
    if (value <= 10) return "text-blue-700";
    if (value <= 20) return "text-amber-700";
    return "text-rose-700";
}

/** Off page one but on page two: the searches nearest a page-one listing. */
export function isCloseToPageOne(k: AeoSearchKeyword): boolean {
    return !k.branded && k.position > 10 && k.position <= 20;
}

const AI_NAMES: Record<string, string> = {
    "chatgpt.com": "ChatGPT",
    "perplexity.ai": "Perplexity",
    "gemini.google.com": "Gemini",
    "copilot.microsoft.com": "Copilot",
};

export const aiName = (source: string) => AI_NAMES[source] ?? source;

/** Sum of a column, or null when any reading in it is unmeasured. */
function total(rows: { sessions: number | null; prevSessions: number | null }[], key: "sessions" | "prevSessions"): number | null {
    let sum = 0;
    for (const r of rows) {
        const v = r[key];
        if (v === null) return null;
        sum += v;
    }
    return sum;
}

/** AI visits now and before, or null when the block is absent. */
export function aiVisits(t: AeoTrafficInsights | undefined): { now: number | null; before: number | null } | null {
    if (!t?.aiReferrals) return null;
    return { now: total(t.aiReferrals, "sessions"), before: total(t.aiReferrals, "prevSessions") };
}

/** The index of the week (Monday start) a date falls in, or -1 when outside the series. */
export function weekIndexOf(weeks: { start?: string }[], date: string): number {
    return weeks.findIndex((w, i) => !!w.start && w.start <= date && (i === weeks.length - 1 ? date < addDays(w.start, 7) : date < (weeks[i + 1].start ?? "")));
}

function addDays(iso: string, days: number): string {
    const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
